import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const NetworkTreeNode = ({ node, level, onNodeClick, isExpanded, onToggleExpand }) => {
  const levelColors = {
    1: { bg: 'bg-primary/10', border: 'border-primary', text: 'text-primary', commission: '10%' },
    2: { bg: 'bg-accent/10', border: 'border-accent', text: 'text-accent', commission: '4%' },
    3: { bg: 'bg-secondary/10', border: 'border-secondary', text: 'text-secondary', commission: '0.6%' },
    4: { bg: 'bg-warning/10', border: 'border-warning', text: 'text-warning', commission: '0.4%' }
  };

  const levelStyle = levelColors?.[level] || levelColors?.[1];
  const hasChildren = node?.children && node?.children?.length > 0;

  return (
    <div className="flex flex-col items-center">
      <div 
        className={`
          relative w-full max-w-xs p-4 md:p-5 lg:p-6 rounded-xl border-2 ${levelStyle?.border} ${levelStyle?.bg}
          transition-smooth hover:elevation-2 cursor-pointer
        `}
        onClick={() => onNodeClick(node)}
      >
        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <div className={`
            px-2 py-1 rounded-lg text-xs font-medium
            ${node?.isActive ? 'bg-success text-success-foreground' : 'bg-muted text-muted-foreground'}
          `}>
            {node?.isActive ? 'Activo' : 'Inactivo'}
          </div>
        </div>

        {/* Profile Section */}
        <div className="flex items-start space-x-3 mb-3">
          <div className="relative flex-shrink-0">
            <Image
              src={node?.avatar}
              alt={node?.avatarAlt}
              className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full object-cover"
            />
            <div className={`
              absolute -bottom-1 -right-1 w-6 h-6 md:w-7 md:h-7 rounded-full ${levelStyle?.bg} border-2 ${levelStyle?.border}
              flex items-center justify-center
            `}>
              <span className={`text-xs font-bold ${levelStyle?.text}`}>N{level}</span>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-foreground text-sm md:text-base truncate">
              {node?.name}
            </h4>
            <p className="text-xs text-muted-foreground">
              {node?.registrationDate}
            </p>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-background/50 rounded-lg p-2">
            <p className="text-xs text-muted-foreground">Referidos</p>
            <p className="text-base md:text-lg font-bold text-foreground">{node?.totalReferrals}</p>
          </div>
          <div className="bg-background/50 rounded-lg p-2">
            <p className="text-xs text-muted-foreground">Ventas</p>
            <p className="text-base md:text-lg font-bold text-foreground">{node?.monthlySales}</p>
          </div>
        </div>

        {/* Commission Info */}
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} color={levelStyle?.text?.replace('text-', 'var(--color-')} />
            <span className={`text-sm font-medium ${levelStyle?.text}`}>
              Comisión {levelStyle?.commission}
            </span>
          </div>
          <div className="text-sm font-bold text-foreground">
            S/ {node?.monthlyCommission?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
          </div>
        </div>

        {/* Expand Button */}
        {hasChildren && (
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onToggleExpand(node?.id);
            }}
            className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-card border-2 border-border rounded-full flex items-center justify-center hover:bg-muted transition-smooth"
          >
            <Icon 
              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
              size={16} 
              color="var(--color-foreground)"
            />
          </button>
        )}
      </div>
      {/* Children Nodes */}
      {hasChildren && isExpanded && (
        <div className="mt-8 flex flex-wrap justify-center gap-4 md:gap-6 lg:gap-8">
          {node?.children?.map((child) => (
            <div key={child?.id} className="relative">
              <div className="absolute -top-8 left-1/2 w-0.5 h-8 bg-border" />
              <NetworkTreeNode
                node={child}
                level={level + 1}
                onNodeClick={onNodeClick}
                isExpanded={isExpanded}
                onToggleExpand={onToggleExpand}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NetworkTreeNode;